// src/components/grammar/GrammarExerciseEngine.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocalizedNavigate } from '../../hooks/useLocalizedNavigate';
import { db, auth } from '../../firebase';
import {
  ArrowLeft,
  ChevronRight,
  ChevronLeft,
  AlertCircle,
  BookOpen,
  HelpCircle,
  Home
} from 'lucide-react';
import GrammarExerciseGuide from './GrammarExerciseGuide';
import GrammarBottomNav from './GrammarBottomNav';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import GrammarExercise from './GrammarExercise';
import { useGamification } from '../../hooks/useGamification';
import { createConfetti } from '../../utils/animations';
import { soundManager, SOUNDS } from '../../utils/soundUtils';
import FloatingXP from '../ui/FloatingXP';
import grammarProgressService from '../../services/grammarProgressService';
import LogamiLogoSpinner from '../ui/LogamiLogoSpinner';
import { getGrammarTopicTitle } from '../../services/grammarTopicsMap';
import { logGrammarPractice } from '../../utils/activityLogger';

const GrammarExerciseEngine = ({
  level,
  pillarId,
  embeddedExercises,
  userProgress,
  pillarConnections = {},
  initialExerciseId,
  initialExerciseIndex,
  onBack,
  onUpdateProgress,
  onGoToLesson,
  onContextUpdate,
  grammarData,
}) => {
  const { t } = useTranslation();
  const navigate = useLocalizedNavigate();
  const [exercises, setExercises] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [currentContext, setCurrentContext] = useState({
    pillarId: null,
    categoryId: null,
    categoryName: null,
    practiceLevel: null,
    practiceLevelName: null,
    structureType: null
  });
  const { awardXP } = useGamification();
  const [showXP, setShowXP] = useState({ show: false, amount: 0, position: null });
  const [showExerciseGuide, setShowExerciseGuide] = useState(false);
  const [hasSeenExerciseGuide, setHasSeenExerciseGuide] = useState(false);

  // Hover states for wave effect buttons
  const [isCheckHovered, setIsCheckHovered] = useState(false);
  const [isRetryHovered, setIsRetryHovered] = useState(false);
  const [isNextHovered, setIsNextHovered] = useState(false);
  const [isLessonHovered, setIsLessonHovered] = useState(false);
  const [isBackHovered, setIsBackHovered] = useState(false);

  const currentExercise = exercises[currentIndex];
  const user = auth.currentUser;

  const detectCurrentContext = useCallback(() => {
    if (!grammarData || !pillarId || !exercises || exercises.length === 0) return;

    const currentExercise = exercises[currentIndex];
    if (!currentExercise?.id) return;

    const context = grammarProgressService.detectExerciseContext(
      grammarData,
      pillarId,
      currentExercise.id
    );

    if (context) {
      const fullContext = {
        pillarId,
        ...context
      };

      setCurrentContext(fullContext);
      console.log('🎯 Exercise context detected:', fullContext);

      if (onContextUpdate) {
        onContextUpdate(fullContext);
      }
    }
  }, [grammarData, pillarId, exercises, currentIndex, onContextUpdate]);


  useEffect(() => {
    detectCurrentContext();
  }, [currentIndex, exercises, detectCurrentContext]);

  useEffect(() => {
    if (embeddedExercises) {
      setExercises(embeddedExercises);
      setLoading(false);
      return;
    }

    const sortAndDiversifyExercises = (exercises) => {
      const difficultyOrder = { 'easy': 1, 'medium': 2, 'hard': 3 };

      const sortedByDifficulty = [...exercises].sort((a, b) => {
        const diffA = difficultyOrder[a.difficulty] || 2;
        const diffB = difficultyOrder[b.difficulty] || 2;
        return diffA - diffB;
      });

      const diversified = [];
      const remaining = [...sortedByDifficulty];

      while (remaining.length > 0) {
        let added = false;

        for (let i = 0; i < remaining.length; i++) {
          const candidate = remaining[i];
          const lastTwo = diversified.slice(-2);

          if (lastTwo.length >= 2 &&
            lastTwo[0].exerciseType === lastTwo[1].exerciseType &&
            candidate.exerciseType === lastTwo[0].exerciseType) {
            continue;
          }

          diversified.push(candidate);
          remaining.splice(i, 1);
          added = true;
          break;
        }

        if (!added && remaining.length > 0) {
          diversified.push(remaining[0]);
          remaining.splice(0, 1);
        }
      }

      return diversified;
    };

    const loadExercises = async () => {
      if (!user || !pillarId) return;

      setLoading(true);
      try {
        const levelLower = level.toLowerCase();
        const response = await fetch(`/data/grammar/grammar-${levelLower}.json`);

        if (response.ok) {
          const data = await response.json();
          const pillarExercises = data.exercises.filter(ex =>
            ex.pillar === pillarId || ex.topic === pillarId
          );

          const sortedExercises = sortAndDiversifyExercises(pillarExercises);
          setExercises(sortedExercises);

          let startIndex = 0;
          if (initialExerciseId) {
            const idx = sortedExercises.findIndex(ex => ex.id === initialExerciseId);
            if (idx !== -1) startIndex = idx;
          } else if (initialExerciseIndex > 0) {
            startIndex = Math.min(initialExerciseIndex, sortedExercises.length - 1);
          }

          setCurrentIndex(startIndex);
        } else {
          console.error('Failed to load grammar data:', response.status);
          setExercises([]);
        }
      } catch (error) {
        console.error('Error loading exercises:', error);
        setExercises([]);
      } finally {
        setLoading(false);
      }
    };

    loadExercises();
  }, [level, pillarId, user, initialExerciseId, initialExerciseIndex]);

  useEffect(() => {
    const savePosition = async () => {
      if (user && exercises.length > 0 && currentIndex >= 0) {
        await grammarProgressService.saveCurrentPosition(
          user.uid,
          level,
          pillarId,
          currentIndex
        );
      }
    };

    const timer = setTimeout(savePosition, 500);
    return () => clearTimeout(timer);
  }, [currentIndex, user, level, pillarId, exercises.length]);

  const handleExerciseGuideComplete = async (dontShowAgain) => {
    const user = auth.currentUser;
    if (dontShowAgain && user) {
      try {
        await updateDoc(doc(db, 'users', user.uid), {
          hasSeenGrammarExerciseGuide: true,
          grammarExerciseGuideCompletedAt: new Date().toISOString()
        });
        setHasSeenExerciseGuide(true);
      } catch (error) {
        console.error('Error saving exercise guide preference:', error);
      }
    }
    setShowExerciseGuide(false);
  };

  const handleShowExerciseGuide = () => {
    setShowExerciseGuide(true);
  };

  const handleExerciseComplete = async (result) => {
    if (!user) return;

    const currentExercise = exercises[currentIndex];

    const xpAmount = result.correct ? 25 : 10;
    setShowXP({
      show: true,
      amount: xpAmount,
      position: { x: window.innerWidth / 2, y: window.innerHeight / 2 }
    });
    await awardXP(xpAmount, 'grammar');

    let correctAnswer = 'N/A';
    let formattedUserAnswer = result.selectedAnswer;

    switch (currentExercise.exerciseType) {
      case 'fillInTheBlanks':
        let userAnswersObj = {};
        try {
          userAnswersObj = JSON.parse(result.selectedAnswer);
        } catch (e) {
          userAnswersObj = result.selectedAnswer;
        }

        const userAnswerParts = [];
        const correctAnswerParts = [];

        if (currentExercise.answers && Array.isArray(currentExercise.answers)) {
          currentExercise.answers.forEach((answer, index) => {
            const blankKey = `blank_${index}`;
            const userValue = userAnswersObj[blankKey] || '(empty)';
            const correctValue = answer.correct || '(empty)';

            userAnswerParts.push(`Blank ${index + 1}: "${userValue}"`);
            correctAnswerParts.push(`Blank ${index + 1}: "${correctValue}"`);
          });

          formattedUserAnswer = userAnswerParts.join(' | ');
          correctAnswer = correctAnswerParts.join(' | ');
        }
        break;

      case 'multipleChoice':
        correctAnswer = currentExercise.correctAnswer || 'N/A';
        break;

      case 'sentenceTransformation':
        correctAnswer = currentExercise.correctAnswer || 'N/A';
        break;

      case 'sentenceBuilding':
        correctAnswer = currentExercise.correctAnswer || 'N/A';
        break;

      case 'errorCorrection':
        correctAnswer = currentExercise.correctStimulus || 'N/A';
        break;

      case 'matchingPairs':
        correctAnswer = 'All pairs matched correctly';
        formattedUserAnswer = result.selectedAnswer;
        break;

      default:
        correctAnswer = currentExercise.correctAnswer || currentExercise.answer || 'N/A';
    }

    await grammarProgressService.updateExerciseCompletion(
      user.uid,
      level,
      pillarId,
      result.exerciseId,
      {
        isCorrect: result.correct,
        userAnswer: formattedUserAnswer,
        correctAnswer: correctAnswer,
        timeSpent: result.timeSpent
      }
    );

    await logGrammarPractice({
      topicId: pillarId,
      topicName: pillarConnections[pillarId]?.title || pillarId,
      timeSpent: result.timeSpent,
      correctAnswers: result.correct ? 1 : 0,
      totalQuestions: 1,
      level: level,
      exerciseType: currentExercise.exerciseType || 'practice'
    });

    if (onUpdateProgress) {
      const updatedProgress = await grammarProgressService.getUserProgress(user.uid, level);
      onUpdateProgress(updatedProgress);
    }
  };

  const handleNext = async () => {
    if (currentIndex < exercises.length - 1) {
      const nextIndex = currentIndex + 1;
      setCurrentIndex(nextIndex);

      await grammarProgressService.saveCurrentPosition(user.uid, level, pillarId, nextIndex);
    } else {
      alert(t('grammar.pillars.completed'));

      await grammarProgressService.clearContinuationPoint(user.uid, level);

      if (onUpdateProgress) {
        const updatedProgress = await grammarProgressService.getUserProgress(user.uid, level);
        onUpdateProgress(updatedProgress);
      }

      onBack();
    }
  };

  const handlePrevious = async () => {
    if (currentIndex > 0) {
      const prevIndex = currentIndex - 1;
      setCurrentIndex(prevIndex);

      await grammarProgressService.saveCurrentPosition(user.uid, level, pillarId, prevIndex);
    }
  };

  const handleToggleBookmark = async () => {
    if (!user) return;
    const currentExercise = exercises[currentIndex];
    const bookmark = {
      exerciseId: currentExercise.id,
      pillarId: pillarId
    };

    await grammarProgressService.toggleBookmark(user.uid, level, bookmark);

    if (onUpdateProgress) {
      const updatedProgress = await grammarProgressService.getUserProgress(user.uid, level);
      onUpdateProgress(updatedProgress);
    }
  };

  const renderExerciseContent = () => {
    const currentExercise = exercises[currentIndex];
    if (!currentExercise) return null;

    const isBookmarked = userProgress?.bookmarks?.some(b => b.exerciseId === currentExercise.id) || false;

    return (
      <GrammarExercise
        key={currentExercise.id}
        exercise={currentExercise}
        onComplete={handleExerciseComplete}
        onNext={handleNext}
        isBookmarked={isBookmarked}
        onToggleBookmark={handleToggleBookmark}
      />
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LogamiLogoSpinner size="xl" decorative={false} alt="Logami loading" />
      </div>
    );
  }

  const topicTitle = getGrammarTopicTitle(level, pillarId) || pillarConnections[pillarId]?.title || null;

  return (
    <div className="max-w-4xl mx-auto px-0 sm:px-6 pb-24 sm:pb-6">
      {/* Topic heading, relates practice to the topic (visible on all screens) */}
      {topicTitle && (
        <div className="mb-3 sm:mb-4 pl-2 sm:pl-0">
          <h1 className="font-space text-lg sm:text-xl font-black text-black tracking-tight truncate" title={topicTitle}>
            {topicTitle}
          </h1>
          <p className="text-xs sm:text-sm text-black/60 mt-0.5">{t('grammar.practice.topic', { defaultValue: 'Practice' })}</p>
        </div>
      )}
      {/* Header, desktop only (mobile uses bottom nav) */}
      <div className="hidden sm:flex mb-4 sm:mb-6 items-center justify-between gap-2 sm:gap-4">
        {/* Back Button with Wave Effect */}
        <button
          onClick={onBack}
          onMouseEnter={() => setIsBackHovered(true)}
          onMouseLeave={() => setIsBackHovered(false)}
          className="inline-flex items-center gap-2 px-3 py-2 bg-white rounded-lg border-2 border-black relative overflow-hidden transition-all duration-300 hover:shadow-lg flex-shrink-0"
        >
          <div
            style={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: 0,
              width: isBackHovered ? '100%' : '0%',
              background: 'linear-gradient(to right, #9ca3af, #4b5563, #000000)',
              transition: 'width 0.7s ease-out',
              zIndex: 0
            }}
          />
          <ArrowLeft
            className="w-5 h-5 relative z-10 transition-colors duration-500"
            style={{ color: isBackHovered ? '#ffffff' : '#000000' }}
          />
          <span
            className="text-sm font-medium relative z-10 transition-colors duration-500"
            style={{ color: isBackHovered ? '#ffffff' : '#000000' }}
          >
            {t('grammar.back')}
          </span>
        </button>

        <button
          onClick={handleShowExerciseGuide}
          className="p-2 bg-gray-100 hover:bg-black hover:text-white rounded-lg transition-all hover:scale-110 flex-shrink-0"
          title="Exercise Help"
        >
          <HelpCircle className="w-5 h-5" />
        </button>

        {pillarConnections[pillarId]?.hasLesson && (
          <button
            onClick={() => onGoToLesson(pillarId)}
            onMouseEnter={() => setIsLessonHovered(true)}
            onMouseLeave={() => setIsLessonHovered(false)}
            className="inline-flex items-center gap-2 px-3 py-2 bg-white rounded-lg border-2 border-black relative overflow-hidden transition-all duration-300 hover:shadow-lg flex-shrink-0"
          >
            <div
              style={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: 0,
                width: isLessonHovered ? '100%' : '0%',
                background: 'linear-gradient(to right, #60a5fa, #3b82f6, #1d4ed8)',
                transition: 'width 0.7s ease-out',
                zIndex: 0
              }}
            />
            <BookOpen
              className="w-4 h-4 relative z-10 transition-colors duration-500"
              style={{ color: isLessonHovered ? '#ffffff' : '#000000' }}
            />
            <span
              className="text-sm font-medium relative z-10 transition-colors duration-500"
              style={{ color: isLessonHovered ? '#ffffff' : '#000000' }}
            >
              Lesson
            </span>
          </button>
        )}
      </div>

      {/* Main Content */}
      {exercises.length > 0 ? (
        <div>
          {/* Progress Bar, desktop only (mobile uses bottom nav ring) */}
          <div className="hidden sm:block mb-4 sm:mb-6 bg-white p-3 sm:p-4 rounded-lg sm:rounded-2xl border-2 sm:border-[3px] border-black relative overflow-hidden">
            {/* Corner decorations */}
            <div className="absolute top-2 left-2 w-2 h-2 border-t-2 border-l-2 border-black/20"></div>
            <div className="absolute bottom-2 right-2 w-2 h-2 border-b-2 border-r-2 border-black/20"></div>

            <div className="flex items-center gap-3 sm:gap-4">
              {/* Previous Button */}
              <button
                onClick={handlePrevious}
                disabled={currentIndex === 0}
                className="p-1.5 sm:p-2 rounded-lg hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
              >
                <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>

              {/* Progress Info */}
              <div className="flex-1">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs sm:text-sm font-space font-bold text-black">
                    Exercise {currentIndex + 1}/{exercises.length}
                  </p>
                  <p className="text-xs sm:text-sm font-medium text-gray-500">
                    {Math.round(((currentIndex + 1) / exercises.length) * 100)}%
                  </p>
                </div>
                {/* Progress Bar */}
                <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                  <div
                    className="h-2 rounded-full transition-all duration-500 ease-out"
                    style={{
                      width: `${((currentIndex + 1) / exercises.length) * 100}%`,
                      background: 'linear-gradient(90deg, #006D77 0%, #83C5BE 100%)'
                    }}
                  />
                </div>
              </div>

              {/* Next Button */}
              <button
                onClick={handleNext}
                className="p-1.5 sm:p-2 rounded-lg hover:bg-gray-100 transition-all"
              >
                <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
            </div>
          </div>

          {/* Exercise Content */}
          {renderExerciseContent()}
        </div>
      ) : (
        <div className="text-center py-12 bg-white rounded-lg sm:rounded-2xl border-2 sm:border-[3px] border-black relative overflow-hidden">
          {/* Corner decorations */}
          <div className="absolute top-2 left-2 w-3 h-3 border-t-2 border-l-2 border-black/20"></div>
          <div className="absolute bottom-2 right-2 w-3 h-3 border-b-2 border-r-2 border-black/20"></div>

          <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-black font-space font-bold text-lg mb-2">
            {t('grammar.exercise.noExercises')}
          </p>
          <p className="text-sm text-gray-500 mb-6">{t('grammar.exercise.checkData')}</p>

          {/* Back button with wave effect */}
          <button
            onClick={onBack}
            onMouseEnter={() => setIsBackHovered(true)}
            onMouseLeave={() => setIsBackHovered(false)}
            className="inline-flex items-center gap-2 px-6 py-3 bg-white rounded-xl border-2 border-black relative overflow-hidden transition-all duration-300 hover:shadow-xl mx-auto"
          >
            <div
              style={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: 0,
                width: isBackHovered ? '100%' : '0%',
                background: 'linear-gradient(to right, #9ca3af, #4b5563, #000000)',
                transition: 'width 0.7s ease-out',
                zIndex: 0
              }}
            />
            <span
              className="font-space font-bold relative z-10 transition-colors duration-500"
              style={{ color: isBackHovered ? '#ffffff' : '#000000' }}
            >
              {t('grammar.back')}
            </span>
          </button>
        </div>
      )}

      {/* Floating XP Component */}
      <FloatingXP
        xp={showXP.amount}
        show={showXP.show}
        position={showXP.position}
        onComplete={() => setShowXP({ show: false, amount: 0, position: null })}
      />

      <GrammarExerciseGuide
        isOpen={showExerciseGuide}
        onClose={async () => {
          setShowExerciseGuide(false);
          setHasSeenExerciseGuide(true);
          localStorage.setItem('hasSeenGrammarExerciseGuide', 'true');
          const u = auth.currentUser;
          if (u) {
            try {
              await updateDoc(doc(db, 'users', u.uid), {
                hasSeenGrammarExerciseGuide: true,
                grammarExerciseGuideCompletedAt: new Date().toISOString()
              });
            } catch (err) {
              console.error('Error saving exercise guide preference:', err);
            }
          }
        }}
        onComplete={handleExerciseGuideComplete}
      />

      {/* Bottom Navigation. Exercise View */}
      {exercises.length > 0 && (
        <GrammarBottomNav
          buttons={[
            {
              id: 'back',
              icon: ArrowLeft,
              label: t('grammar.back'),
              onClick: onBack
            },
            {
              id: 'home',
              icon: Home,
              label: 'Home',
              onClick: () => navigate('/dashboard')
            },
            {
              id: 'progress',
              type: 'progress',
              current: currentIndex + 1,
              total: exercises.length,
              progress: Math.round(((currentIndex + 1) / exercises.length) * 100),
              color: '#006D77'
            },
            {
              id: 'next',
              icon: ChevronRight,
              label: t('grammar.exercise.next', { defaultValue: 'Next' }),
              onClick: handleNext
            },
            pillarConnections[pillarId]?.hasLesson ? {
              id: 'lesson',
              icon: BookOpen,
              label: t('grammar.lesson', { defaultValue: 'Lesson' }),
              onClick: () => onGoToLesson(pillarId)
            } : {
              id: 'help',
              icon: HelpCircle,
              label: t('grammar.guide.help', { defaultValue: 'Help' }),
              onClick: handleShowExerciseGuide
            }
          ]}
        />
      )}
    </div>
  );
};

export default GrammarExerciseEngine;